import type { Field } from 'payload'

import deepMerge from '@/utilities/deepMerge'
import { ButtonProps } from '@/components/ui/button'

export type LinkAppearances =
  | 'default'
  | 'outline'
  | 'inline'
  | Exclude<ButtonProps['variant'], undefined | null>

export const appearanceOptions: Record<LinkAppearances, { label: string; value: string }> = {
  default: {
    label: 'Default',
    value: 'default',
  },
  outline: {
    label: 'Outline',
    value: 'outline',
  },
  inline: {
    label: 'Inline',
    value: 'inline',
  },
  black: {
    label: 'Black',
    value: 'black',
  },
  destructive: {
    label: 'Destructive',
    value: 'destructive',
  },
  ghost: {
    label: 'Ghost',
    value: 'ghost',
  },
  link: {
    label: 'Link',
    value: 'link',
  },
  secondary: {
    label: 'Secondary',
    value: 'secondary',
  },
}

type LinkFieldFactory = (options?: {
  appearances?: LinkAppearances[] | false
  disableLabel?: boolean
  overrides?: Record<string, unknown>
  className?: string
}) => Field

type GetLinkUrlLinkParameter = {
  url?: string | null
  reference?: {
    value?:
      | string
      | number
      | null
      | {
          slug?: string | null
        }
  } | null
}

export function getLinkUrl(link: GetLinkUrlLinkParameter) {
  if (link.url) {
    return link.url
  }
  if (link.reference) {
    const slug =
      typeof link.reference?.value !== 'object'
        ? String(link.reference.value)
        : link.reference?.value?.slug
    if (slug === 'home') {
      return '/'
    } else if (slug) {
      return `/${slug}`
    }
  }
  return '/'
}

export const link: LinkFieldFactory = ({
  appearances,
  disableLabel = false,
  overrides = {},
} = {}) => {
  const linkResult: Field = {
    name: 'link',
    type: 'group',
    admin: {
      hideGutter: true,
    },
    fields: [
      {
        type: 'row',
        fields: [
          {
            name: 'type',
            type: 'radio',
            admin: {
              layout: 'horizontal',
              width: '50%',
            },
            defaultValue: 'reference',
            options: [
              {
                label: 'Internal link',
                value: 'reference',
              },
              {
                label: 'Custom URL',
                value: 'custom',
              },
            ],
          },
          {
            name: 'newTab',
            type: 'checkbox',
            admin: {
              style: {
                alignSelf: 'flex-end',
              },
              width: '50%',
            },
            label: 'Open in new tab',
          },
        ],
      },
    ],
  }

  const linkTypes: Field[] = [
    {
      name: 'reference',
      type: 'relationship',
      admin: {
        condition: (_, siblingData) => siblingData?.type === 'reference',
      },
      label: 'Document to link to',
      maxDepth: 1,
      relationTo: ['pages'],
      required: true,
    },
    {
      name: 'url',
      type: 'text',
      admin: {
        condition: (_, siblingData) => siblingData?.type === 'custom',
      },
      label: 'Custom URL',
      required: true,
    },
  ]

  if (!disableLabel) {
    linkTypes.map((linkType) => ({
      ...linkType,
      admin: {
        ...linkType.admin,
        width: '50%',
      },
    }))

    linkResult.fields.push({
      type: 'row',
      fields: [
        ...linkTypes,
        {
          name: 'label',
          type: 'text',
          admin: {
            width: '50%',
          },
          label: 'Label',
          required: true,
        },
      ],
    })
  } else {
    linkResult.fields = [...linkResult.fields, ...linkTypes]
  }

  linkResult.fields.push({
    type: 'row',
    fields: [
      {
        name: 'appearance',
        type: 'select',
        admin: {
          description: 'Choose how the link should be rendered.',
          width: '50%',
        },
        defaultValue: 'default',
        options: Object.values(appearanceOptions),
      },
      {
        name: 'action',
        type: 'checkbox',
        admin: {
          width: '50%',
          style: {
            marginTop: '20px',
          },
        },
      },
      {
        name: 'className',
        type: 'text',
      },
    ],
  })

  return deepMerge(linkResult, overrides)
}
